import { textGenerationClient } from '@/integrations/clients';
import { IntegrationError } from '@/integrations/shared';
import { useAIOperationWrapper } from '@/hooks/ai-usage/useAIOperationWrapper';
import type { TextGenerationRequest, TextAssistant, TextPrompt } from '@/integrations/openai';

export function useTextGeneration() {
  const { executeAIOperation, isLoading, error } = useAIOperationWrapper();

  const generateText = async (request: TextGenerationRequest): Promise<string> => {
    return executeAIOperation(
      () => textGenerationClient.generateText(request),
      {
        immediate: true, // Text generation usually needs immediate feedback
        errorPrefix: 'Text generation failed',
        debug: false
      }
    );
  };

  const getAssistants = async (): Promise<TextAssistant[]> => {
    try {
      return await textGenerationClient.getAssistants();
    } catch (err: any) {
      const errorMessage = err instanceof IntegrationError 
        ? err.message 
        : (err.message || 'Failed to fetch assistants');
      console.error('Error fetching assistants:', err);
      throw new Error(errorMessage);
    }
  };

  const getPrompts = async (): Promise<TextPrompt[]> => {
    try {
      return await textGenerationClient.getPrompts();
    } catch (err: any) {
      const errorMessage = err instanceof IntegrationError 
        ? err.message 
        : (err.message || 'Failed to fetch prompts');
      console.error('Error fetching prompts:', err);
      throw new Error(errorMessage);
    }
  };

  return {
    generateText,
    getAssistants,
    getPrompts,
    isLoading,
    error,
  };
}
